import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import unitConversionService, { UnitConversion } from '../../services/unitConversionService';
import './ProductCategoryListPage.css';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2 } from 'lucide-react';

const UnitConversionListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState<UnitConversion[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await unitConversionService.getUnitConversions(1, 100);
      if (result?.data) {
        setItems(result.data);
      }
      setLoading(false);
    } catch (err) {
      setError(t('unitConversion.failedToLoadConversion'));
      setLoading(false);
    }
  };

  const handleDelete = (id: number) => {
    navigate(`/products/unit-conversions/${id}/delete`);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const result = await unitConversionService.getUnitConversions(1, 100);
      if (result?.data) {
        setItems(result.data);
      }
    } catch (err) {
      setError(t('unitConversion.failedToLoadConversion'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'fromUnitName',
      label: t('unitConversion.fromUnit'),
      sortable: true,
      width: '200px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'toUnitName',
      label: t('unitConversion.toUnit'),
      sortable: true,
      width: '200px',
      render: (value) => <span className="description-text">{value}</span>,
    },
    {
      key: 'conversionFactor',
      label: t('unitConversion.conversionFactor'),
      sortable: true,
      width: '150px',
    },
    {
      key: 'isActive',
      label: t('common.status'),
      sortable: true,
      width: '120px',
      hideMobile: true,
      render: (value) => (
        <span className={`badge ${value ? 'badge-active' : 'badge-inactive'}`}>
          {value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/products/unit-conversions/${item.unitConversionId}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    {
      icon: (
        <SquarePen size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/products/unit-conversions/${item.unitConversionId}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
    {
      icon: (
        <Trash2 size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => handleDelete(item.unitConversionId),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    },
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('unitConversion.title')}</h1>
          <div className="header-actions">
            <button
              className="btn-new"
              onClick={() => navigate('/products/unit-conversions/create')}
              title={t('unitConversion.newConversion')}
            >
              <CirclePlus/>
              {t('unitConversion.newConversion')}
            </button>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('unitConversion.refreshConversions')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('unitConversion.refreshConversions')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <DataGrid
          data={items}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('unitConversion.noConversionsFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default UnitConversionListPage;
